/**
 * GP-07 conveyor window: queue items ride a belt; only the first `slots` are tappable
 * (core rule), the next few show dimmed so players can plan.
 */
import * as Phaser from 'phaser';
import { PIECE_BOARD, type CompiledLevel, type Round } from '@mp/core';
import { app } from '../game/context.js';
import type { Rect } from '../game/layout.js';
import { C } from '../game/theme.js';
import { vp } from '../game/viewport.js';
import { dur } from '../fx/motion.js';
import { TileView } from './board.js';
import { convStart } from './dispensers.js';
import { NO_LOOKS, type ItemLooks } from './looks.js';

export class ConveyorView {
  private views = new Map<number, TileView>();
  /** lockKey: does piece i wear the key badge (the board's rule, so both hold it until the key pops)? */
  keyOf: ((r: Round, i: number) => boolean) | null = null;
  private size: number;
  private step: number;
  private visible: number;

  constructor(
    private scene: Phaser.Scene,
    private L: CompiledLevel,
    private area: Rect,
    round: Round,
    /** Clearer items: the art each item is drawn with (looks.ts); every item as itself by default */
    private looks: ItemLooks = NO_LOOKS,
  ) {
    const u = vp.u;
    this.size = Math.min(area.h * 0.8, 72 * u);
    this.step = this.size * 1.08;
    this.visible = Math.max(L.convSlots, Math.floor((area.w - this.size * 0.4) / this.step));
    const f = app().art.get('prp_mech_conveyor');
    const belt = scene.add.image(area.x + area.w / 2, area.y + area.h / 2, f.texture, f.frame).setDepth(40);
    belt.setDisplaySize(area.w, area.h * 0.9);
    // tappable window highlight
    const g = scene.add.graphics().setDepth(41);
    g.lineStyle(3 * u, C.mustard, 1);
    g.strokeRoundedRect(this.slotX(0) - this.size * 0.56, area.y + area.h / 2 - this.size * 0.58, this.step * (L.convSlots - 1) + this.size * 1.12, this.size * 1.16, 12 * u);
    this.sync(round, false);
  }

  private slotX(k: number): number {
    return this.area.x + this.size * 0.7 + k * this.step;
  }

  /** Queue position k (0 = front) → centre. */
  posOfSlot(k: number): { x: number; y: number } {
    return { x: this.slotX(k), y: this.area.y + this.area.h / 2 };
  }

  private queue(r: Round): number[] {
    const out: number[] = [];
    // queue entries only (dispenser items follow the conveyor's pieces)
    const s = convStart(this.L);
    for (let i = s; i < s + this.L.nConv; i++) if (r.piece[i] === PIECE_BOARD) out.push(i);
    return out;
  }

  sync(r: Round, animate: boolean): void {
    const q = this.queue(r);
    const d = animate ? dur(200) : 0;
    q.forEach((piece, k) => {
      if (k >= this.visible) {
        this.views.get(piece)?.setVisible(false);
        return;
      }
      let v = this.views.get(piece);
      const p = this.posOfSlot(k);
      if (!v) {
        v = new TileView(this.scene, piece, false, this.size);
        v.setItem(this.looks.of(this.L.items[r.item[piece]!]!));
        v.setPosition(p.x + (animate ? this.step : 0), p.y).setDepth(50);
        this.views.set(piece, v);
      }
      v.setVisible(true);
      v.setCovered(k >= this.L.convSlots, animate);
      v.setKey(this.keyOf?.(r, piece) ?? false, animate);
      if (d) this.scene.tweens.add({ targets: v, x: p.x, y: p.y, duration: d, ease: 'Cubic.easeOut' });
      else v.setPosition(p.x, p.y);
    });
  }

  hitTest(x: number, y: number, r: Round): number {
    const q = this.queue(r);
    for (let k = 0; k < Math.min(q.length, this.visible); k++) {
      const p = this.posOfSlot(k);
      if (Math.abs(x - p.x) <= this.size / 2 && Math.abs(y - p.y) <= this.size / 2) return q[k]!;
    }
    return -1;
  }

  pos(piece: number, r: Round): { x: number; y: number } | null {
    const k = this.queue(r).indexOf(piece);
    return k >= 0 && k < this.visible ? this.posOfSlot(k) : null;
  }

  detach(piece: number): TileView | null {
    const v = this.views.get(piece) ?? null;
    this.views.delete(piece);
    v?.lightUp();
    v?.setKey(false, false);
    return v;
  }

  /** Key badges on the belt (test hooks). */
  keysShown(): number {
    let n = 0;
    for (const v of this.views.values()) if (v.visible && v.keyBadge) n++;
    return n;
  }

  attach(piece: number, v: TileView): void {
    this.views.set(piece, v);
    v.setScale(1);
  }
}
